import { createAxiosClient, ServiceProxyMap } from '@atlassian/temp-client-utils';
import { PaymentMethod, generatedTypesNew } from '@css-xp/graphtypes';
import {
  CreatePaymentMethodRequest,
  PaymentMethodRequest,
  setPricingEligibility,
  UpdateBillToAndShipToParties,
  UpdateTransactionAccountPayload,
  UpdateTransactionAccountResponseToClient,
  CreateTreansactionAccountV2,
  TransactionAccountAuditLogRecords,
  BillToAndShipToParties,
} from '@atlassian/cssxp-types-billing';

interface UpdateMultipleShipToParties {
  shipToAddresses: (BillToAndShipToParties & {
    pricingEligibility: Record<string, string>;
  })[];
  transactionAccountId: string;
}

const client = createAxiosClient({ baseURL: '/api/1/transaction-account' }, ServiceProxyMap.ANS);

export const updateTransactionAccountDetails = async (
  transactionAccountId: string,
  requestPayload: UpdateTransactionAccountPayload
): Promise<UpdateTransactionAccountResponseToClient> => {
  const response = await client.post(`/update/${transactionAccountId}`, requestPayload);
  return response.data;
};

export const createTransactionAccountDetails = async (
  requestPayload: UpdateTransactionAccountPayload
): Promise<UpdateTransactionAccountResponseToClient> => {
  const response = await client.post(`/create`, requestPayload);
  return response.data;
};

export const createTransactionAccountDetailsV2 = async (
  requestPayload: CreateTreansactionAccountV2
): Promise<UpdateTransactionAccountResponseToClient> => {
  const response = await client.post(`/v2/create`, requestPayload);
  return response.data;
};

export const updateBillToParty = async (requestPayload: UpdateBillToAndShipToParties): Promise<string> => {
  const response = await client.post(`/update/bill-to-party/${requestPayload.transactionAccountId}`, requestPayload);
  return response.data;
};

export const updateShipToParty = async (requestPayload: UpdateBillToAndShipToParties): Promise<string> => {
  const response = await client.post(`/update/ship-to-party/${requestPayload.transactionAccountId}`, requestPayload);
  return response.data;
};

export const updateShipToParties = async (requestPayload: UpdateMultipleShipToParties): Promise<string> => {
  const response = await client.post(`/update/ship-to-parties/${requestPayload.transactionAccountId}`, requestPayload);
  return response.data;
};

export const setDefaultPaymentMethod = async (requestPayload: PaymentMethodRequest): Promise<PaymentMethod> => {
  const response = await client.post(
    `/update/${requestPayload.transactionAccountId}/payment-method/${requestPayload.id}`,
    requestPayload
  );
  return response.data;
};

export const updatePricingEligibility = async (
  requestPayload: setPricingEligibility
): Promise<UpdateTransactionAccountResponseToClient> => {
  const response = await client.put(`/update/price-eligibility/${requestPayload.transactionAccountId}`, requestPayload);
  return response.data;
};

export const updateShipToPricingEligibility = async (
  requestPayload: setPricingEligibility
): Promise<UpdateTransactionAccountResponseToClient> => {
  const response = await client.put(
    `/update/ship-to-price-eligibility/${requestPayload.transactionAccountId}`,
    requestPayload
  );
  return response.data;
};

export const createPaymentMethod = async (requestPayload: CreatePaymentMethodRequest): Promise<PaymentMethod> => {
  const response = await client.post(
    `/create/${requestPayload.transactionAccountId}/payment-method`,
    requestPayload.data
  );
  return response.data;
};

export const getTaxExemptionAuditLogs = async (transactionAccountNumber: string) => {
  const response = await client.get(`/taxExemptionLogs/${transactionAccountNumber}`);
  return response.data;
};

export const getTransactionAccountAuditLogs = async (
  transactionAccountId: string
): Promise<TransactionAccountAuditLogRecords> => {
  const response = await client.get(`/txa-audit-logs/${transactionAccountId}`);
  return response.data;
};

export const getTransactionAccountBalance = async (transactionAccountId: string, currency?: string) => {
  const accountBalanceCurrency = currency ? currency?.toUpperCase() : generatedTypesNew.Currency.USD;
  const response = await client.get(`/account-balance/${transactionAccountId}?currency=${accountBalanceCurrency}`);
  return response.data;
};
